import { pubsub } from '@services';
import { d as debounce } from './debounce-Dg7s1ivP.js';

class FormMultiRangeSlider extends HTMLElement {
  /** @type {HTMLDivElement} */ #slider = this.querySelector('[data-slot="slider"]');
  /** @type {HTMLSpanElement} */ #track = this.querySelector('[data-slot="track"]');
  /** @type {HTMLSpanElement} */ #range = this.querySelector('[data-slot="range"]');
  /** @type {HTMLSpanElement[]} */ #thumbs = [...this.querySelectorAll('[data-slot="thumb"]')];
  /** @type {number} */ #thumbWidth = Number(this.getAttribute('data-thumb-width'));
  /** @type {HTMLInputElement} */ #inputMin = this.querySelector('[data-slot="input-min"]');
  /** @type {HTMLInputElement} */ #inputMax = this.querySelector('[data-slot="input-max"]');
  /** value domain from aria-valuemin/aria-valuemax (absolute units) */
  #minValue = Number(this.#thumbs[0].getAttribute('aria-valuemin'));
  #maxValue = Number(this.#thumbs[0].getAttribute('aria-valuemax'));
  /** step size in absolute units */
  #step = Number(this.getAttribute('data-step'));
  /** active thumb index during drag */
  #activeIndex = 0;
  /** minimum spacing between thumbs in absolute units (0 allows touch/cross) */
  #minGap = 1;

  #pageKeys = new Set(['PageUp', 'PageDown']);
  #posKeys = new Set(['ArrowRight', 'ArrowUp', 'PageUp']);
  #negKeys = new Set(['ArrowLeft', 'ArrowDown', 'PageDown']);
  /** debounce delay for typing (ms) */
  #debounceMs = Number(this.getAttribute('data-input-debounce')) || 500;
  /** debounced input handlers per field (min=0, max=1) */
  #debouncedInputFns = [null, null];

  // Read current values from aria-valuenow
  get values() {
    return this.#thumbs.map((thumb) => Number(thumb.getAttribute('aria-valuenow')) || 0);
  }

  connectedCallback() {
    // Centralized pointer handling on the slider element (like Reka UI)
    this.#slider.addEventListener('pointerdown', (e) => this.#onPointerDown(e));
    this.#slider.addEventListener('pointermove', (e) => this.#onPointerMove(e));
    this.#slider.addEventListener('pointerup', (e) => this.#onPointerUp(e));

    // Keyboard handling on the slider; active thumb determined by focus or pointer logic
    this.#slider.addEventListener('keydown', (e) => this.#onKeyDown(e));

    // Keep active index in sync with focused thumb for keyboard interactions
    this.#thumbs.forEach((thumb, index) => {
      thumb.addEventListener('focus', () => (this.#activeIndex = index));
    });

    // Wire input <-> slider two-way sync if inputs exist (assumes order: [min, max])
    ['input', 'change'].forEach((type) => {
      this.#inputMin?.addEventListener(type, (e) => {
        this.#activeIndex = 0;
        this.#onNumberInput(e);
      });
      this.#inputMax?.addEventListener(type, (e) => {
        this.#activeIndex = 1;
        this.#onNumberInput(e);
      });
    });

    // Prepare debounced input handlers
    if (this.#inputMin) {
      this.#debouncedInputFns[0] = debounce(() => {
        this.#activeIndex = 0;
        this.#applyFromInput(this.#inputMin);
      }, this.#debounceMs);
    }
    if (this.#inputMax) {
      this.#debouncedInputFns[1] = debounce(() => {
        this.#activeIndex = 1;
        this.#applyFromInput(this.#inputMax);
      }, this.#debounceMs);
    }
  }

  /**
   * Slider-level pointer handling (applies to both track and thumbs)
   * @param {PointerEvent} event
   */
  #onPointerDown(event) {
    event.preventDefault();
    const { pointerId, target } = event;
    target.setPointerCapture(pointerId);
    // If a thumb was pressed, just focus it and set active index; movement handled in move
    const thumbIndex = this.#thumbs.indexOf(target);
    if (thumbIndex !== -1) {
      target.focus();
      this.#activeIndex = thumbIndex;
      return;
    }
    // Otherwise, start from track position: pick closest thumb and update
    this.#startDragFromTrack(event);
  }

  /**
   * Pointer move handling (applies to both track and thumbs)
   * @param {PointerEvent} event
   */
  #onPointerMove(event) {
    const { pointerId, target } = event;
    if (target.hasPointerCapture(pointerId)) this.#drag(event);
  }

  /**
   * Pointer up handling (applies to both track and thumbs)
   * @param {PointerEvent} event
   */
  #onPointerUp(event) {
    const { pointerId, target } = event;
    if (!target.hasPointerCapture(pointerId)) return;
    target.releasePointerCapture(pointerId);
    this.#stopDrag();
  }

  /**
   * Keyboard handling (Home, End, Arrows, PageUp/Down, Shift+Arrows)
   * @param {KeyboardEvent} event
   */
  #onKeyDown(event) {
    const { key, target } = event;
    // Only respond to keys when a thumb is focused within this slider
    if (!this.contains(target)) return;
    if (!this.#thumbs.includes(target)) return;

    if (key === 'Home') return this.#onHome(event);
    if (key === 'End') return this.#onEnd(event);
    return this.#onStep(event);
  }

  /**
   * Home key handling
   * @param {KeyboardEvent} event
   */
  #onHome(event) {
    event.preventDefault();
    this.#applyNextValue(this.#minValue);
  }

  /**
   * End key handling
   * @param {KeyboardEvent} event
   */
  #onEnd(event) {
    event.preventDefault();
    this.#applyNextValue(this.#maxValue);
  }

  /**
   * Handle numeric input changes for min/max fields
   * @param {Event} event
   */
  #onNumberInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    if (event.type === 'change') {
      // Cancel pending debounced apply and apply immediately
      this.#debouncedInputFns[this.#activeIndex]?.cancel?.();
      this.#applyFromInput(input);
    } else {
      // Debounce typing
      this.#debouncedInputFns[this.#activeIndex]?.();
    }
  }

  #applyFromInput(input) {
    const raw = Number(input.value);
    if (Number.isNaN(raw)) return;
    const next = this.#clampAndSnap(raw);
    this.#applyNextValue(next, { focus: false });
  }

  /**
   * Step key handling
   * @param {KeyboardEvent} event
   */
  #onStep(event) {
    const { key, shiftKey } = event;
    const isArrow = key.startsWith('Arrow');
    const isPage = this.#pageKeys.has(key);
    if (!isArrow && !isPage) return;
    event.preventDefault();
    const isSkip = isPage || (shiftKey && isArrow);
    const multiplier = isSkip ? 10 : 1;
    const direction = this.#getDirectionForKey(key);
    if (!direction) return;
    const current = this.values[this.#activeIndex];
    const next = current + this.#step * multiplier * direction;
    this.#applyNextValue(next);
  }

  /**
   * Get the direction of the key
   * @param {string} key
   * @returns {number}
   */
  #getDirectionForKey(key) {
    if (this.#posKeys.has(key)) return 1;
    if (this.#negKeys.has(key)) return -1;
    return 0;
  }

  /**
   * Determine closest thumb and begin drag from track interaction
   * @param {PointerEvent} event
   */
  #startDragFromTrack(event) {
    // Use a raw value (no thumb width compensation) to choose closest thumb
    const provisional = this.#valueFromEventRaw(event);
    this.#activeIndex = this.#getClosestValueIndex(provisional);
    // Recompute using active thumb width so cursor aligns with thumb center visually
    const nextValue = this.#valueFromEventForThumb(event);
    this.#applyNextValue(nextValue);
  }

  /**
   * Update during drag
   * @param {PointerEvent} event
   */
  #drag(event) {
    const nextValue = this.#valueFromEventForThumb(event);
    this.#applyNextValue(nextValue);
  }

  // End drag (placeholder for commit event hook if needed)
  #stopDrag() {
    pubsub.publish('form-multi-range-slider:stop-drag', this.values);
  }

  // Core handoff logic adapted from Reka UI approach
  #applyNextValue(nextValue, { focus = true } = {}) {
    const prevValues = this.values;
    const nextValues = this.#getNextSortedValues(prevValues, nextValue);
    if (!this.#hasMinGap(nextValues)) return;
    // Handoff on crossing: track new index of the moved value
    this.#activeIndex = nextValues.indexOf(nextValue);
    this.#setValues(nextValues);
    if (focus) this.#thumbs[this.#activeIndex]?.focus();
  }

  // Convert pointer position → absolute value using full track width (no thumb width compensation)
  #valueFromEventRaw({ clientX }) {
    const { left, width } = this.#track.getBoundingClientRect();
    const ratio = (clientX - left) / width;
    const clampedRatio = Math.max(0, Math.min(1, ratio));
    const rawValue = this.#minValue + clampedRatio * (this.#maxValue - this.#minValue);
    const stepped = Math.round(rawValue / this.#step) * this.#step;
    return Math.max(this.#minValue, Math.min(this.#maxValue, stepped));
  }

  // Convert pointer position → absolute value compensating for thumb width so center aligns to cursor
  #valueFromEventForThumb({ clientX }) {
    const { left, width: trackWidth } = this.#track.getBoundingClientRect();
    const half = this.#thumbWidth / 2;
    const available = Math.max(1, trackWidth - this.#thumbWidth);
    const x = clientX - left;
    const ratio = (x - half) / available;
    const clampedRatio = Math.max(0, Math.min(1, ratio));
    const rawValue = this.#minValue + clampedRatio * (this.#maxValue - this.#minValue);
    const stepped = Math.round(rawValue / this.#step) * this.#step;
    return Math.max(this.#minValue, Math.min(this.#maxValue, stepped));
  }

  // Write values back to DOM: thumb positions, aria, and range fill
  #setValues(values) {
    // Ensure sorted copy to position thumbs consistently
    const sorted = [...values].sort((a, b) => a - b);
    // Direction is always LTR in this project
    const direction = 1;

    this.#thumbs.forEach((thumb, i) => {
      const value = values[i];
      thumb.setAttribute('aria-valuenow', value);
      const percent = this.#convertValueToPercentage(value);
      const offsetPx = this.#getThumbInBoundsOffset(percent, direction);
      thumb.style.left = `calc(${percent}% + ${offsetPx}px)`;
    });

    // Update range fill based on sorted bounds
    const leftPercent = this.#convertValueToPercentage(sorted[0]);
    const rightPercent = 100 - this.#convertValueToPercentage(sorted[1]);
    this.#range.style.left = `${leftPercent}%`;
    this.#range.style.right = `${rightPercent}%`;

    // Reflect values to numeric inputs if present (min at [0], max at [1])
    this.#inputMin.value = sorted[0];
    this.#inputMax.value = sorted[1];
  }

  // Helpers analogous to Reka utils
  #getNextSortedValues(prevValues = [], nextValue) {
    const nextValues = [...prevValues];
    nextValues[this.#activeIndex] = nextValue;
    return nextValues.sort((a, b) => a - b);
  }

  #getClosestValueIndex(nextValue) {
    const distances = this.values?.map((v) => Math.abs(v - nextValue));
    return distances.indexOf(Math.min(...distances));
  }

  #hasMinGap(values) {
    if (values.length < 2) return true;
    const gaps = values.slice(0, -1).map((v, i) => values[i + 1] - v);
    const actualMin = Math.min(...gaps);
    return actualMin >= this.#minGap;
  }

  // Keeps the thumb's center within the track bounds when near edges
  #getThumbInBoundsOffset(leftPercent, direction) {
    const halfWidth = this.#thumbWidth / 2;
    // Simplified linear mapping equivalent to Reka's linearScale usage:
    // leftPercent=0 => +halfWidth, 50 => 0, 100 => -halfWidth
    const offset = halfWidth * (1 - leftPercent / 50);
    return offset * direction;
  }

  // Convert absolute value to percentage of domain
  #convertValueToPercentage(value) {
    const maxSteps = this.#maxValue - this.#minValue;
    if (maxSteps === 0) return 0;
    const percentPerStep = 100 / maxSteps;
    const percentage = percentPerStep * (value - this.#minValue);
    return Math.max(0, Math.min(100, percentage));
  }

  // Clamp to [minValue, maxValue] and snap to step
  #clampAndSnap(value) {
    const snapped = Math.round(value / this.#step) * this.#step;
    return Math.max(this.#minValue, Math.min(this.#maxValue, snapped));
  }
}

customElements.define('form-multi-range-slider', FormMultiRangeSlider);
